import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';

import axios from 'axios';
import { toast } from 'sonner';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from './ui/card';
import { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from './ui/tabs';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import GridPagination from './GridPagination';
import useClientPagination from '../hooks/useClientPagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import Autocomplete from './ui/autocomplete';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { 
  FileText,
  Upload,
  Send,
  Eye,
  CheckCircle,
  Clock,
  AlertTriangle,
  XCircle,
  Download,
  FileSignature,
  Shield,
  Plus,
  RefreshCw,
  Search,
  Calendar,
  User,
  Mail,
  UserPlus,
  X,
  Copy,
  QrCode,
  Link,
  Share,
  ExternalLink,
  Trash2,
  Loader2
} from 'lucide-react';
import { validatePhone, formatBrazilianPhone, formatCPF, formatPhone, validateCPF } from '../lib/helpers';

// ─── Componente memoizado: cards de signatários ───
const SignerCards = React.memo(({ signers, employees, onUpdate, onRemove, onAdd, onEmployeeSelect }) => {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="text-base font-semibold">Signatários</Label>
        <Button type="button" variant="outline" size="sm" onClick={onAdd} className="flex items-center space-x-1">
          <UserPlus className="h-4 w-4" />
          <span>Adicionar Signatário</span>
        </Button>
      </div>
      <div className="max-h-[400px] overflow-y-auto pr-2 space-y-4">
        {signers.map((signer, index) => (
          <div key={index} className="p-4 border rounded-lg bg-gray-50">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium">Signatário {index + 1}</h4>
              {signers.length > 1 && (
                <Button type="button" variant="outline" size="sm" onClick={() => onRemove(index)} className="text-red-600 hover:text-red-700">
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nome Completo *</Label>
                <Input value={signer.signer_name} onChange={(e) => onUpdate(index, 'signer_name', e.target.value)} placeholder="Nome completo" required />
              </div>
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input type="email" value={signer.signer_email} onChange={(e) => onUpdate(index, 'signer_email', e.target.value)} placeholder="email@exemplo.com" required />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-3">
              <div className="space-y-2">
                <Label>Telefone *</Label>
                <Input value={signer.signer_phone} onChange={(e) => onUpdate(index, 'signer_phone', formatPhone(e.target.value))} placeholder="(11) 99999-9999" maxLength={16} required />
              </div>
              <div className="space-y-2">
                <Label>CPF *</Label>
                <Input value={signer.signer_cpf} onChange={(e) => onUpdate(index, 'signer_cpf', formatCPF(e.target.value))} placeholder="000.000.000-00" maxLength={14} required />
              </div>
            </div>
            <div className="space-y-2 mt-3">
              <Label>Colaborador (opcional)</Label>
              <Autocomplete
                value={signer.employee_id}
                onValueChange={(value) => onEmployeeSelect(index, value)}
                options={employees}
                getOptionValue={(emp) => emp.id}
                getOptionLabel={(emp) => `${emp.nome} - ${emp.cargo}`}
                placeholder="Selecione se for funcionário"
                emptyMessage="Nenhum funcionário encontrado"
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
});

// ─── Carrega o pdf.js (via CDN) uma única vez, sob demanda ───
const PDFJS_VERSION = '3.11.174';
let pdfjsLoadingPromise = null;
const ensurePdfJs = () => {
  if (window.pdfjsLib) return Promise.resolve(window.pdfjsLib);
  if (pdfjsLoadingPromise) return pdfjsLoadingPromise;
  pdfjsLoadingPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VERSION}/pdf.min.js`;
    script.onload = () => {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VERSION}/pdf.worker.min.js`;
      resolve(window.pdfjsLib);
    };
    script.onerror = () => reject(new Error('Falha ao carregar visualizador de PDF'));
    document.body.appendChild(script);
  });
  return pdfjsLoadingPromise;
};

const STAMP_WIDTH_PT = 200; // largura do carimbo de assinatura, em pontos PDF (deve bater com o backend)
const STAMP_HEIGHT_PT = 60; // altura do carimbo de assinatura, em pontos PDF (deve bater com o backend)

// ─── Seletor de posição da assinatura: clique no documento (ou arraste o marcador) para escolher onde o carimbo aparece ───
const SignaturePositionPicker = ({ documentId, initialPosition, onConfirm, onClose }) => {
  const canvasRef = useRef(null);
  const draggingRef = useRef(false);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pageNum, setPageNum] = useState(initialPosition?.signature_page || 1);
  const [numPages, setNumPages] = useState(1);
  const [pageSize, setPageSize] = useState({ width: 0, height: 0 }); // em pontos PDF
  const [scale, setScale] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [marker, setMarker] = useState(null); // { x, y } em pixels do canvas renderizado

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const pdfjsLib = await ensurePdfJs();
        const response = await axios.get(`${API}/api/signatures/documents/${documentId}/view`, {
          responseType: 'arraybuffer'
        });
        if (cancelled) return;
        const doc = await pdfjsLib.getDocument({ data: response.data }).promise;
        if (cancelled) return;
        setPdfDoc(doc);
        setNumPages(doc.numPages);
        setPageNum((p) => Math.min(Math.max(p, 1), doc.numPages));
      } catch (err) {
        console.error('Erro ao carregar PDF para posicionamento:', err);
        if (!cancelled) setError('Não foi possível carregar o documento para posicionar a assinatura.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [documentId]);

  useEffect(() => {
    if (!pdfDoc) return;
    let cancelled = false;
    (async () => {
      const page = await pdfDoc.getPage(pageNum);
      if (cancelled) return;
      const baseViewport = page.getViewport({ scale: 1 });
      setPageSize({ width: baseViewport.width, height: baseViewport.height });

      const containerWidth = Math.min(760, window.innerWidth - 120);
      const renderScale = containerWidth / baseViewport.width;
      setScale(renderScale);

      const viewport = page.getViewport({ scale: renderScale });
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      const ctx = canvas.getContext('2d');
      await page.render({ canvasContext: ctx, viewport }).promise;

      // Se já havia posição escolhida nesta página, mostrar o marcador
      if (initialPosition?.signature_page === pageNum && initialPosition?.signature_x != null && initialPosition?.signature_y != null) {
        const stampCenterXPt = initialPosition.signature_x + STAMP_WIDTH_PT / 2;
        const stampCenterYPt = initialPosition.signature_y + STAMP_HEIGHT_PT; // topo do carimbo
        setMarker({
          x: stampCenterXPt * renderScale,
          y: (baseViewport.height - stampCenterYPt) * renderScale
        });
      } else {
        setMarker(null);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfDoc, pageNum]);

  const clampMarker = (x, y) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x, y };
    const halfW = (STAMP_WIDTH_PT / 2) * scale;
    const boxH = STAMP_HEIGHT_PT * scale;
    return {
      x: Math.max(halfW, Math.min(x, canvas.width - halfW)),
      y: Math.max(0, Math.min(y, canvas.height - boxH))
    };
  };

  const pointFromEvent = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const point = e.touches && e.touches.length > 0 ? e.touches[0] : e;
    return {
      x: (point.clientX - rect.left) * (canvas.width / rect.width),
      y: (point.clientY - rect.top) * (canvas.height / rect.height)
    };
  };

  const handleCanvasClick = (e) => {
    if (draggingRef.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const { x, y } = pointFromEvent(e);
    setMarker(clampMarker(x, y));
  };

  // Arrastar o marcador já colocado para ajuste fino (mouse ou toque)
  const startDrag = (e) => {
    e.stopPropagation();
    e.preventDefault();
    draggingRef.current = true;

    const move = (moveEvent) => {
      const { x, y } = pointFromEvent(moveEvent);
      setMarker(clampMarker(x, y));
    };
    const stop = () => {
      // Pequeno delay para o click subsequente no canvas (que dispara logo após o mouseup) ser ignorado
      setTimeout(() => { draggingRef.current = false; }, 0);
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', stop);
      window.removeEventListener('touchmove', move);
      window.removeEventListener('touchend', stop);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', stop);
    window.addEventListener('touchmove', move, { passive: false });
    window.addEventListener('touchend', stop);
  };

  const handleConfirm = () => {
    if (!marker || !pageSize.width) {
      toast.error('Clique no documento para marcar onde a assinatura deve aparecer');
      return;
    }
    // Canvas: origem no topo-esquerdo, Y cresce para baixo. PDF: origem embaixo-esquerda, Y cresce para cima.
    const clickXPt = marker.x / scale;
    const clickYPt = pageSize.height - (marker.y / scale);

    // Centraliza o carimbo (200x60pt) sob o ponto clicado, sem deixar sair da página
    let boxX = clickXPt - STAMP_WIDTH_PT / 2;
    let boxY = clickYPt - STAMP_HEIGHT_PT;
    boxX = Math.max(0, Math.min(boxX, pageSize.width - STAMP_WIDTH_PT));
    boxY = Math.max(0, Math.min(boxY, pageSize.height - STAMP_HEIGHT_PT));

    onConfirm({ signature_page: pageNum, signature_x: boxX, signature_y: boxY });
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Posição da assinatura no documento</DialogTitle>
          <DialogDescription>
            Clique no local do documento para posicionar o carimbo, ou arraste-o para ajustar.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-16 text-gray-500">
            <Loader2 className="h-6 w-6 animate-spin mr-2" /> Carregando documento...
          </div>
        )}

        {!loading && error && (
          <div className="py-8 text-center text-red-600 text-sm">{error}</div>
        )}

        {!loading && !error && (
          <>
            <div className="flex items-center justify-between mb-2">
              <Button type="button" variant="outline" size="sm" disabled={pageNum <= 1} onClick={() => setPageNum((p) => p - 1)}>
                Página anterior
              </Button>
              <span className="text-sm text-gray-600">Página {pageNum} de {numPages}</span>
              <Button type="button" variant="outline" size="sm" disabled={pageNum >= numPages} onClick={() => setPageNum((p) => p + 1)}>
                Próxima página
              </Button>
            </div>

            <div className="relative border rounded-lg overflow-auto max-h-[70vh] mx-auto" style={{ width: 'fit-content' }}>
              <canvas ref={canvasRef} onClick={handleCanvasClick} className="cursor-crosshair block" />
              {marker && (
                <div
                  onMouseDown={startDrag}
                  onTouchStart={startDrag}
                  className="absolute border-2 border-purple-600 bg-purple-600/10 rounded cursor-move touch-none flex items-center justify-center"
                  style={{
                    left: marker.x - (STAMP_WIDTH_PT / 2) * scale,
                    top: marker.y,
                    width: STAMP_WIDTH_PT * scale,
                    height: STAMP_HEIGHT_PT * scale
                  }}
                >
                  <span className="text-[10px] font-medium text-purple-700 bg-white/80 px-1 rounded pointer-events-none select-none">
                    arraste para ajustar
                  </span>
                </div>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-2 text-center">
              A caixa roxa mostra o tamanho aproximado do carimbo de assinatura. Clique em outro ponto para mover, ou arraste a caixa.
            </p>
          </>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
          <Button type="button" onClick={handleConfirm} disabled={loading || !!error}>Confirmar posição</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── Componente memoizado: tabela de documentos ───
const DocumentsTable = React.memo(({ documents, selectedDocs, canSelectDoc, onToggleDoc, onSelectAll, getStatusBadge, formatDate, formatFileSize, copyValidationCode, copyValidationUrl, openValidationPage, onView, onDownload, onDelete }) => {
  const selectable = documents.filter(canSelectDoc);
  const allSelected = documents.length > 0 && selectable.length > 0 && selectable.every(d => selectedDocs.has(d.id));

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-10">
            <input type="checkbox" className="h-4 w-4 rounded border-gray-300" checked={allSelected} onChange={onSelectAll} />
          </TableHead>
          <TableHead>Documento</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Assinaturas</TableHead>
          <TableHead>Validação Pública</TableHead>
          <TableHead>Criado em</TableHead>
          <TableHead>Tamanho</TableHead>
          <TableHead>Ações</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {documents.map(document => {
          const isSelected = selectedDocs.has(document.id);
          const selectable = canSelectDoc(document);
          return (
            <TableRow key={document.id} className={`${isSelected ? 'bg-purple-50' : ''} ${!selectable ? 'opacity-50' : ''}`}>
              <TableCell>
                <input type="checkbox" className="h-4 w-4 rounded border-gray-300" checked={isSelected} onChange={() => onToggleDoc(document.id)} disabled={!selectable} />
              </TableCell>
              <TableCell>
                <p className="font-medium">{document.title}</p>
                {document.description && <p className="text-sm text-gray-500">{document.description}</p>}
              </TableCell>
              <TableCell>{getStatusBadge(document.status)}</TableCell>
              <TableCell>
                <span className="text-green-600 font-medium">{document.completed_signatures || 0}</span>
                <span className="text-gray-400"> / </span>
                <span className="text-yellow-600">{document.pending_signatures || 0}</span>
                <span className="text-gray-400"> pendentes</span>
              </TableCell>
              <TableCell>
                {document.validation_code ? (
                  <div className="flex items-center space-x-1 flex-wrap gap-1">
                    <Badge variant="outline" className="text-xs font-mono bg-blue-50 text-blue-700 border-blue-200">{document.validation_code}</Badge>
                    <Button variant="ghost" size="sm" onClick={() => copyValidationCode(document.validation_code)} title="Copiar código" className="h-6 px-1"><Copy className="h-3 w-3" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => copyValidationUrl(document.validation_code)} title="Copiar link" className="h-6 px-1"><Link className="h-3 w-3" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => openValidationPage(document.validation_code)} title="Abrir validação" className="h-6 px-1"><ExternalLink className="h-3 w-3" /></Button>
                  </div>
                ) : (
                  <span className="text-gray-400 text-sm">Não disponível</span>
                )}
              </TableCell>
              <TableCell className="text-sm whitespace-nowrap">{formatDate(document.created_at)}</TableCell>
              <TableCell className="text-sm">{formatFileSize(document.file_size)}</TableCell>
              <TableCell>
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => onView(document.id)} title="Visualizar"><Eye className="h-4 w-4" /></Button>
                  {document.completed_signatures > 0 ? (
                    <>
                      <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => onDownload(document, false)} title="Original"><Download className="h-4 w-4" /></Button>
                      <Button variant="default" size="sm" className="h-8 w-8 p-0 bg-green-600 hover:bg-green-700" onClick={() => onDownload(document, true)} title="Assinado"><Download className="h-4 w-4" /></Button>
                    </>
                  ) : (
                    <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => onDownload(document, false)} title="Baixar"><Download className="h-4 w-4" /></Button>
                  )}
                  {(document.signature_requests_count || 0) === 0 && (document.pending_signatures || 0) === 0 && (document.completed_signatures || 0) === 0 && (
                    <Button variant="outline" size="sm" className="h-8 w-8 p-0 text-red-600" onClick={() => onDelete(document)} title="Excluir"><Trash2 className="h-4 w-4" /></Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
});

const API = process.env.REACT_APP_BACKEND_URL;

const DigitalSignaturePage = () => {
  const { user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [documentStats, setDocumentStats] = useState({ total_documents: 0, pending_signature: 0, signed: 0 });
  const [employees, setEmployees] = useState([]);
  const [signatureRequests, setSignatureRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('documents');
  const allowedTabs = ['documents', 'requests', 'audit', 'audit-advanced'];

  // Busca client-side por aba
  const [searchDocuments, setSearchDocuments] = useState('');
  const [searchRequests, setSearchRequests] = useState('');
  const [searchAuditLogs, setSearchAuditLogs] = useState('');
  
  // Modals
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showUrlsModal, setShowUrlsModal] = useState(false);
  const [signingUrls, setSigningUrls] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [selectedDocs, setSelectedDocs] = useState(new Set());
  const [showBatchSignModal, setShowBatchSignModal] = useState(false);
  const [batchSending, setBatchSending] = useState(false);

  // Paginação client-side da grid principal de documentos
  const filteredDocuments = useMemo(() => {
    const term = searchDocuments.trim().toLowerCase();
    if (!term) return documents;
    return documents.filter(doc =>
      (doc.title || '').toLowerCase().includes(term) ||
      (doc.description || '').toLowerCase().includes(term) ||
      (doc.status || '').toLowerCase().includes(term) ||
      (doc.validation_code || '').toLowerCase().includes(term)
    );
  }, [documents, searchDocuments]);

  const {
    paginated: documentsPaginated,
    pagination: documentsPagination,
    setPage: setDocumentsPage,
  } = useClientPagination(filteredDocuments, 10);

  // Paginação client-side da grid de requisições de assinatura
  const filteredSignatureRequests = useMemo(() => {
    const term = searchRequests.trim().toLowerCase();
    if (!term) return signatureRequests;
    return signatureRequests.filter(request =>
      (request.document?.title || '').toLowerCase().includes(term) ||
      (request.signer_name || '').toLowerCase().includes(term) ||
      (request.signer_email || '').toLowerCase().includes(term) ||
      (request.status || '').toLowerCase().includes(term)
    );
  }, [signatureRequests, searchRequests]);

  const {
    paginated: signatureRequestsPaginated,
    pagination: signatureRequestsPagination,
    setPage: setSignatureRequestsPage,
  } = useClientPagination(filteredSignatureRequests, 10);

  // Form states
  const [uploadForm, setUploadForm] = useState({
    title: '',
    description: '',
    file: null
  });
  const [uploading, setUploading] = useState(false);

  const [requestForm, setRequestForm] = useState({
    document_id: '',
    signers: [
      {
        signer_name: '',
        signer_email: '',
        signer_phone: '',
        signer_cpf: '',
        employee_id: ''
      }
    ],
    send_via_email: true,
    send_via_whatsapp: false,
    require_token_validation: true,
    signature_page: null,
    signature_x: null,
    signature_y: null
  });
  const [showPositionPicker, setShowPositionPicker] = useState(false);

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadDocuments(),
        loadDocumentStats(),
        loadEmployees(),
        loadSignatureRequests(),
        loadAuditLogs()
      ]);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados do sistema');
    } finally {
      setLoading(false);
    }
  };

  const loadDocuments = async () => {
    try {
      const response = await axios.get(`${API}/api/signatures/documents`);
      setDocuments(response.data);
    } catch (error) {
      console.error('Erro ao carregar documentos:', error);
      if (error.response?.status === 403) {
        toast.error('Você não tem permissão para gerenciar assinaturas eletrônicas');
      }
    }
  };

  const loadDocumentStats = async () => {
    try {
      const response = await axios.get(`${API}/api/signatures/documents/stats`);
      setDocumentStats(response.data);
    } catch (error) {
      console.error('Erro ao carregar estatísticas de documentos:', error);
    }
  };

  const loadEmployees = async () => {
    try {
      const response = await axios.get(`${API}/api/employees`);
      setEmployees(response.data.filter(emp => emp.situacao === 'ATIVO'));
    } catch (error) {
      console.error('Erro ao carregar funcionários:', error);
    }
  };

  const loadSignatureRequests = async () => {
    try {
      const response = await axios.get(`${API}/api/signatures/requests`);
      setSignatureRequests(response.data);
    } catch (error) {
      console.error('Erro ao carregar solicitações:', error);
      if (error.response?.status === 403) {
        toast.error('Você não tem permissão para visualizar solicitações de assinatura');
      } else {
        // Tratamento melhorado do erro
        let errorMessage = 'Erro ao carregar solicitações';
        
        if (error.response?.data) {
          const errorData = error.response.data;
          if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          } else if (errorData.message) {
            errorMessage = errorData.message;
          } 
        }
        
        toast.error(errorMessage);
      }
    }
  };

  const handleUploadDocument = async (e) => {
    e.preventDefault();

    if (uploading) return; // Evita duplo submit (duplo clique / Enter + clique)

    if (!uploadForm.file || !uploadForm.title) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    // Validar tamanho do arquivo (30MB)
    const maxSize = 30 * 1024 * 1024; // 30MB
    if (uploadForm.file.size > maxSize) {
      toast.error(`Arquivo muito grande. Tamanho máximo: 30MB. Seu arquivo tem ${(uploadForm.file.size / (1024 * 1024)).toFixed(2)}MB.`);
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadForm.file);
      formData.append('title', uploadForm.title);
      if (uploadForm.description) {
        formData.append('description', uploadForm.description);
      }

      await axios.post(`${API}/api/signatures/documents/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Documento carregado com sucesso!');
      setShowUploadModal(false);
      setUploadForm({ title: '', description: '', file: null });
      await loadDocuments();
    } catch (error) {
      console.error('Erro ao carregar documento:', error);
      
      // Tratamento melhorado do erro
      let errorMessage = 'Erro ao carregar documento';
      
      if (error.response?.data) {
        const errorData = error.response.data;
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail)) {
          // Se for um array de erros de validação
          errorMessage = errorData.detail.map(err => 
            typeof err === 'string' ? err : err.msg || 'Erro de validação'
          ).join(', ');
        } else if (typeof errorData.detail === 'object') {
          // Se for um objeto de erro
          errorMessage = errorData.detail.msg || errorData.detail.message || 'Erro de validação';
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const addSigner = () => {
    setRequestForm({
      ...requestForm,
      signers: [
        ...requestForm.signers,
        {
          signer_name: '',
          signer_email: '',
          signer_phone: '',
          signer_cpf: '',
          employee_id: ''
        }
      ]
    });
  };

  const removeSigner = (index) => {
    if (requestForm.signers.length > 1) {
      const newSigners = requestForm.signers.filter((_, i) => i !== index);
      setRequestForm({
        ...requestForm,
        signers: newSigners
      });
    }
  };

  const updateSigner = (index, field, value) => {
    const newSigners = [...requestForm.signers];
    newSigners[index] = {
      ...newSigners[index],
      [field]: value
    };
    setRequestForm({
      ...requestForm,
      signers: newSigners
    });
  };

  const handleCreateSignatureRequest = async (e) => {
    e.preventDefault();
    
    if (!requestForm.document_id) {
      toast.error('Selecione um documento');
      return;
    }

    // Validar todos os signatários
    for (let i = 0; i < requestForm.signers.length; i++) {
      const signer = requestForm.signers[i];
      
      // Validar campos obrigatórios (incluindo telefone)
      if (!signer.signer_name || !signer.signer_email || !signer.signer_cpf || !signer.signer_phone) {
        toast.error(`Preencha todos os campos obrigatórios do signatário ${i + 1} (Nome, E-mail, CPF e Telefone)`);
        return;
      }

      // Validar formato do telefone
      if (!validatePhone(signer.signer_phone)) {
        toast.error(`Número de telefone inválido para signatário ${i + 1}. Use formato brasileiro: (11) 99999-9999`);
        return;
      }

      const cpfDigits = signer.signer_cpf.replace(/\D/g, '');
      if (cpfDigits.length === 11 && !validateCPF(cpfDigits)) {
        toast.error(`CPF inválido para signatário ${i + 1}`);
        return;
      }
      
      // Validar formato do e-mail
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(signer.signer_email)) {
        toast.error(`E-mail inválido para signatário ${i + 1}`);
        return;
      }
    }

    try {
      // Criar solicitações para cada signatário
      const requests = [];
      
      for (const signer of requestForm.signers) {
        // Formatar telefone brasileiro
        const formattedRequest = {
          document_id: requestForm.document_id,
          signer_name: signer.signer_name,
          signer_email: signer.signer_email,
          signer_phone: signer.signer_phone ? formatBrazilianPhone(signer.signer_phone) : '',
          signer_cpf: signer.signer_cpf,
          employee_id: signer.employee_id,
          signature_page: requestForm.signature_page,
          signature_x: requestForm.signature_x,
          signature_y: requestForm.signature_y,
          send_via_email: requestForm.send_via_email,
          send_via_whatsapp: requestForm.send_via_whatsapp
        };

        const response = await axios.post(`${API}/api/signatures/requests`, formattedRequest);
        requests.push({
          signer_name: signer.signer_name,
          signing_url: response.data.signing_url,
          response: response.data
        });
      }
      
      toast.success(`${requests.length} solicitação(ões) de assinatura criada(s) com sucesso!`);
      setShowRequestModal(false);
      
      // Reset form
      setRequestForm({
        document_id: '',
        signers: [
          {
            signer_name: '',
            signer_email: '',
            signer_phone: '',
            signer_cpf: '',
            employee_id: ''
          }
        ],
        send_via_email: true,
        send_via_whatsapp: false,
        signature_page: null,
        signature_x: null,
        signature_y: null
      });

      // Mostrar URLs de assinatura para cópia
      if (requests.length === 1) {
        const signingUrl = `${window.location.origin}${requests[0].signing_url}`;
        if (navigator.clipboard) {
          await navigator.clipboard.writeText(signingUrl);
          toast.success('URL de assinatura copiada para área de transferência!');
        }
      } else {
        // Para múltiplos destinatários, mostrar modal com todos os links
        setSigningUrls(requests);
        setShowUrlsModal(true);
      }

      await loadDocuments();
      await loadSignatureRequests();
    } catch (error) {
      console.error('Erro ao criar solicitação:', error);
      
      // Tratamento melhorado do erro
      let errorMessage = 'Erro ao criar solicitação de assinatura';
      
      if (error.response?.data) {
        const errorData = error.response.data;
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail)) {
          // Se for um array de erros de validação
          errorMessage = errorData.detail.map(err => 
            typeof err === 'string' ? err : err.msg || 'Erro de validação'
          ).join(', ');
        } else if (typeof errorData.detail === 'object') {
          // Se for um objeto de erro
          errorMessage = errorData.detail.msg || errorData.detail.message || 'Erro de validação';
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      }
      
      toast.error(errorMessage);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'draft': { variant: 'secondary', label: 'Rascunho', color: 'bg-gray-500', icon: FileText },
      'pending_signature': { variant: 'default', label: 'Aguardando Assinatura', color: 'bg-yellow-500', icon: Clock },
      'signed': { variant: 'default', label: 'Assinado', color: 'bg-green-500', icon: CheckCircle },
      'cancelled': { variant: 'destructive', label: 'Cancelado', color: 'bg-red-500', icon: XCircle },
      'expired': { variant: 'destructive', label: 'Expirado', color: 'bg-red-500', icon: AlertTriangle }
    };
    
    const config = statusConfig[status] || statusConfig['draft'];
    const Icon = config.icon;
    
    return (
      <Badge variant={config.variant} className="flex items-center space-x-1">
        <Icon className="h-3 w-3" />
        <span>{config.label}</span>
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const METADATA_LABELS = {
    filename: 'Nome do Arquivo',
    size: 'Tamanho',
    gridfs_id: 'ID de Armazenamento',
    document_id: 'ID do Documento',
    document_title: 'Documento',
    signer_name: 'Signatário',
    signer_email: 'E-mail do Signatário',
    reason: 'Motivo',
    ip_address: 'Endereço IP',
    user_agent: 'Navegador/Dispositivo',
  };

  const formatMetadataKey = (key) => METADATA_LABELS[key] || key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  const formatMetadataValue = (key, value) => {
    if (key === 'size' && typeof value === 'number') return formatFileSize(value);
    if (value && typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  const handleViewDocument = async (documentId) => {
    try {
      // Fazer requisição autenticada para obter o blob do documento
      const response = await axios.get(`${API}/api/signatures/documents/${documentId}/view`, {
        responseType: 'blob'
      });
      
      // Criar URL do blob para visualização
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      
      // Abrir em nova aba
      window.open(url, '_blank');
      
      // Limpar URL após um tempo
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 1000);
      
    } catch (error) {
      console.error('Erro ao visualizar documento:', error);
      
      let errorMessage = 'Erro ao abrir documento para visualização';
      if (error.response?.data) {
        const errorData = error.response.data;
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      }
      
      toast.error(errorMessage);
    }
  };

  const handleDownloadDocument = async (documentData, isSignedVersion = false, signatureRequestId = null) => {
    try {
      // Validar se documentData existe e tem ID
      if (!documentData || !documentData.id) {
        toast.error('Dados do documento inválidos');
        return;
      }

      let endpoint;
      let fileName;

      if (isSignedVersion) {
        // Download da versão assinada — com signatureRequestId, traz só o carimbo daquele signatário
        endpoint = `${API}/api/signatures/documents/${documentData.id}/download-signed`;
        if (signatureRequestId) {
          endpoint += `?signature_request_id=${encodeURIComponent(signatureRequestId)}`;
        }
        fileName = `${documentData.title}_ASSINADO.pdf`;
      } else {
        // Download da versão original
        endpoint = `${API}/api/signatures/documents/${documentData.id}/download`;
        fileName = `${documentData.title}.pdf`;
      }
      
      console.log('🔍 Fazendo download:', {
        endpoint,
        fileName,
        isSignedVersion,
        documentId: documentData.id
      });
      
      const response = await axios.get(endpoint, {
        responseType: 'blob',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      console.log('✅ Download realizado:', response.status, response.data.size, 'bytes');
      
      // Criar link para download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      
      // Disparar download
      document.body.appendChild(link);
      link.click();
      
      // Limpar
      link.remove();
      window.URL.revokeObjectURL(url);
      
      const message = isSignedVersion ? 'Download do documento assinado iniciado!' : 'Download do documento iniciado!';
      toast.success(message);
      
    } catch (error) {
      console.error('❌ Erro ao fazer download do documento:', error);
      console.error('❌ Status:', error.response?.status);
      console.error('❌ Dados do erro:', error.response?.data);
      
      let errorMessage = 'Erro ao tentar realizar o download';
      
      // Mensagens de erro mais específicas baseadas no status
      if (error.response?.status === 401) {
        errorMessage = 'Sem autorização para download. Faça login novamente.';
      } else if (error.response?.status === 404) {
        if (isSignedVersion) {
          errorMessage = 'Documento assinado não encontrado. Verifique se o documento foi assinado.';
        } else {
          errorMessage = 'Documento não encontrado.';
        }
      } else if (error.response?.status === 403) {
        errorMessage = 'Sem permissão para fazer download deste documento.';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = `Erro de rede: ${error.message}`;
      }
      
      toast.error(errorMessage);
    }
  };

  const loadAuditLogs = async () => {
    try {
      const response = await axios.get(`${API}/api/signatures/audit-logs`);
      setAuditLogs(response.data);
    } catch (error) {
      console.error('Erro ao carregar logs de auditoria:', error);
    }
  };

  const filteredAuditLogs = useMemo(() => {
    const term = searchAuditLogs.trim().toLowerCase();
    if (!term) return auditLogs;
    return auditLogs.filter(log =>
      (log.action || '').toLowerCase().includes(term) ||
      (log.description || '').toLowerCase().includes(term) ||
      (log.document_title || '').toLowerCase().includes(term) ||
      (log.signer_name || '').toLowerCase().includes(term) ||
      (log.user_name || '').toLowerCase().includes(term) ||
      (log.ip_address || '').toLowerCase().includes(term)
    );
  }, [auditLogs, searchAuditLogs]);

  const handleSendEmail = async (requestId) => {
    try {
      await axios.post(`${API}/api/signatures/send-email/${requestId}`);
      toast.success('Email enviado com sucesso!');
      await loadSignatureRequests();
    } catch (error) {
      console.error('Erro ao enviar email:', error);
      
      // Tratamento melhorado do erro
      let errorMessage = 'Erro ao enviar email';
      
      if (error.response?.data) {
        const errorData = error.response.data;
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map(err => 
            typeof err === 'string' ? err : err.msg || 'Erro de validação'
          ).join(', ');
        } else if (typeof errorData.detail === 'object') {
          errorMessage = errorData.detail.msg || errorData.detail.message || 'Erro de validação';
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      }
      
      toast.error(errorMessage);
    }
  };

  const handleCancelRequest = async (requestId) => {
    if (!window.confirm('Tem certeza que deseja cancelar esta solicitação?')) {
      return;
    }

    try {
      await axios.delete(`${API}/api/signatures/requests/${requestId}`);
      toast.success('Solicitação cancelada com sucesso!');
      await loadSignatureRequests();
      await loadDocuments();
    } catch (error) {
      console.error('Erro ao cancelar solicitação:', error);
      
      // Tratamento melhorado do erro
      let errorMessage = 'Erro ao cancelar solicitação';
      
      if (error.response?.data) {
        const errorData = error.response.data;
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map(err => 
            typeof err === 'string' ? err : err.msg || 'Erro de validação'
          ).join(', ');
        } else if (typeof errorData.detail === 'object') {
          errorMessage = errorData.detail.msg || errorData.detail.message || 'Erro de validação';
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      }
      
      toast.error(errorMessage);
    }
  };

  const handleDeleteRequest = async (requestId) => {
    if (!window.confirm('⚠️ ATENÇÃO: Esta ação irá excluir permanentemente a solicitação.\n\nEsta ação não pode ser desfeita. Tem certeza que deseja continuar?')) {
      return;
    }

    try {
      await axios.delete(`${API}/api/signatures/requests/${requestId}/delete`);
      toast.success('Solicitação excluída permanentemente!');
      await loadSignatureRequests();
      await loadDocuments();
    } catch (error) {
      console.error('Erro ao excluir solicitação:', error);
      
      // Tratamento melhorado do erro
      let errorMessage = 'Erro ao excluir solicitação';
      
      if (error.response?.data) {
        const errorData = error.response.data;
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map(err => 
            typeof err === 'string' ? err : err.msg || 'Erro de validação'
          ).join(', ');
        } else if (typeof errorData.detail === 'object') {
          errorMessage = errorData.detail.msg || errorData.detail.message || 'Erro de validação';
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      }
      
      toast.error(errorMessage);
    }
  };

  const handleDeleteDocument = async (document) => {
    // Verificar se o documento tem solicitações
    const hasRequests = (document.signature_requests_count || 0) > 0 || 
                        (document.pending_signatures || 0) > 0 || 
                        (document.completed_signatures || 0) > 0;
    
    if (hasRequests) {
      toast.error('Não é possível excluir este documento. Existem solicitações de assinatura vinculadas.');
      return;
    }
    
    if (!window.confirm(`⚠️ ATENÇÃO: Esta ação irá excluir permanentemente o documento "${document.title}".\n\nEsta ação não pode ser desfeita. Tem certeza que deseja continuar?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/api/signatures/documents/${document.id}`);
      toast.success('Documento excluído com sucesso!');
      await loadDocuments();
    } catch (error) {
      console.error('Erro ao excluir documento:', error);
      
      let errorMessage = 'Erro ao excluir documento';
      
      if (error.response?.data) {
        const errorData = error.response.data;
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      }
      
      toast.error(errorMessage);
    }
  };

  const canSelectDoc = (doc) => doc.status === 'draft';

  const toggleDocSelection = useCallback((docId) => {
    const doc = documents.find(d => d.id === docId);
    if (!doc || !canSelectDoc(doc)) return;
    setSelectedDocs(prev => {
      const next = new Set(prev);
      if (next.has(docId)) next.delete(docId); else next.add(docId);
      return next;
    });
  }, [documents]);

  const selectAllDocs = useCallback(() => {
    const selectable = documentsPaginated.filter(canSelectDoc);
    if (selectable.every(d => selectedDocs.has(d.id))) {
      setSelectedDocs(new Set());
    } else {
      setSelectedDocs(new Set(selectable.map(d => d.id)));
    }
  }, [documentsPaginated, selectedDocs]);

  const selectedDocumentsList = useMemo(
    () => documents.filter(d => selectedDocs.has(d.id) && canSelectDoc(d)),
    [documents, selectedDocs]
  );

  const getSelectedDocuments = useCallback(() => selectedDocumentsList, [selectedDocumentsList]);
  const selectedDocsCount = selectedDocumentsList.length;

  const handleBatchSignatureRequest = async (e) => {
    e.preventDefault();
      const selectedDocsList = selectedDocumentsList;
    if (selectedDocsList.length === 0) {
      toast.error('Nenhum documento disponível para assinatura. Selecione apenas documentos em status "Rascunho".');
      return;
    }

    const validSigners = requestForm.signers.filter(s => s.signer_name && s.signer_email && s.signer_cpf && s.signer_phone);
    if (validSigners.length === 0) {
      toast.error('Adicione pelo menos um signatário com todos os campos obrigatórios');
      return;
    }

    setBatchSending(true);
    try {
      let totalRequests = 0;
      const allUrls = [];

      for (const doc of selectedDocsList) {
        for (const signer of validSigners) {
          const payload = {
            document_id: doc.id,
            signer_name: signer.signer_name,
            signer_email: signer.signer_email,
            signer_phone: signer.signer_phone ? formatBrazilianPhone(signer.signer_phone) : '',
            signer_cpf: signer.signer_cpf,
            employee_id: signer.employee_id,
            send_via_email: requestForm.send_via_email,
            send_via_whatsapp: requestForm.send_via_whatsapp
          };

          const response = await axios.post(`${API}/api/signatures/requests`, payload);
          allUrls.push({
            signer_name: signer.signer_name,
            signing_url: response.data.signing_url,
            document_title: doc.title,
            response: response.data
          });
          totalRequests++;
        }
      }

      toast.success(`${totalRequests} solicitação(ões) criada(s) para ${selectedDocsList.length} documento(s)!`);
      setShowBatchSignModal(false);
      setSelectedDocs(new Set());
      setRequestForm({
        document_id: '',
        signers: [{ signer_name: '', signer_email: '', signer_phone: '', signer_cpf: '', employee_id: '' }],
        send_via_email: true,
        send_via_whatsapp: false
      });

      if (allUrls.length > 0) {
        setSigningUrls(allUrls);
        setShowUrlsModal(true);
      }

      await loadDocuments();
      await loadSignatureRequests();
    } catch (error) {
      console.error('Erro no envio em lote:', error);
      toast.error(error.response?.data?.detail || 'Erro ao enviar documentos para assinatura');
    }
    setBatchSending(false);
  };

  const getRequestStatusBadge = (status) => {
    const statusConfig = {
      'pending': { variant: 'secondary', label: 'Pendente', color: 'bg-gray-500', icon: Clock },
      'sent': { variant: 'default', label: 'Enviado', color: 'bg-blue-500', icon: Send },
      'viewed': { variant: 'default', label: 'Visualizado', color: 'bg-yellow-500', icon: Eye },
      'signed': { variant: 'default', label: 'Assinado', color: 'bg-green-500', icon: CheckCircle },
      'expired': { variant: 'destructive', label: 'Expirado', color: 'bg-red-500', icon: AlertTriangle },
      'cancelled': { variant: 'destructive', label: 'Cancelado', color: 'bg-red-500', icon: XCircle }
    };
    
    const config = statusConfig[status] || statusConfig['pending'];
    const Icon = config.icon;
    
    return (
      <Badge variant={config.variant} className="flex items-center space-x-1">
        <Icon className="h-3 w-3" />
        <span>{config.label}</span>
      </Badge>
    );
  };

  const copyValidationCode = (validationCode) => {
    if (validationCode) {
      navigator.clipboard.writeText(validationCode);
      toast.success('Código de validação copiado!');
    } else {
      toast.error('Código de validação não disponível');
    }
  };

  const copyValidationUrl = (validationCode) => {
    if (validationCode) {
      const baseUrl = process.env.REACT_APP_BASE_URL || window.location.origin;
      const url = `${baseUrl}/validate/${validationCode}`;
      navigator.clipboard.writeText(url);
      toast.success('Link de validação copiado!');
    } else {
      toast.error('Código de validação não disponível');
    }
  };

  const openValidationPage = (validationCode) => {
    if (validationCode) {
      const baseUrl = process.env.REACT_APP_BASE_URL || window.location.origin;
      const url = `${baseUrl}/validate/${validationCode}`;
      window.open(url, '_blank');
    } else {
      toast.error('Código de validação não disponível');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span>Carregando sistema de assinaturas...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 flex items-center space-x-2">
            <FileSignature className="h-8 w-8 text-blue-600" />
            <span>Assinatura Eletrônica</span>
          </h1>
          <p className="text-slate-600 mt-1">
            Sistema completo de documentos digitais com validade jurídica
          </p>
        </div>
        
        <div className="flex items-center space-x-2">
          <Button 
            variant="outline" 
            onClick={loadInitialData}
            disabled={loading}
            className="flex items-center space-x-2"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Atualizar</span>
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center space-x-2">
              <FileText className="h-8 w-8 text-blue-600" />
              <div>
                <p className="text-2xl font-bold">{documentStats.total_documents}</p>
                <p className="text-xs text-gray-500">Total de Documentos</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center space-x-2">
              <Clock className="h-8 w-8 text-yellow-600" />
              <div>
                <p className="text-2xl font-bold">
                  {documentStats.pending_signature}
                </p>
                <p className="text-xs text-gray-500">Aguardando Assinatura</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center space-x-2">
              <CheckCircle className="h-8 w-8 text-green-600" />
              <div>
                <p className="text-2xl font-bold">
                  {documentStats.signed}
                </p>
                <p className="text-xs text-gray-500">Assinados</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center space-x-2">
              <Shield className="h-8 w-8 text-purple-600" />
              <div>
                <p className="text-2xl font-bold">100%</p>
                <p className="text-xs text-gray-500">Segurança Legal</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          {allowedTabs.includes('documents') && <TabsTrigger value="documents">Documentos</TabsTrigger>}
          {allowedTabs.includes('requests') && <TabsTrigger value="requests">Solicitações</TabsTrigger>}
          {allowedTabs.includes('audit') && <TabsTrigger value="audit">Auditoria</TabsTrigger>}
          {allowedTabs.includes('audit-advanced') && <TabsTrigger value="audit-advanced">Auditoria Avançada</TabsTrigger>}
        </TabsList>

        {/* Documents Tab */}
        {allowedTabs.includes('documents') && (
        <TabsContent value="documents" className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <h2 className="text-xl font-semibold">Gestão de Documentos</h2>
              <div className="flex items-center space-x-2">
                <Search className="h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Buscar por título, descrição ou status..."
                  value={searchDocuments}
                  onChange={(e) => setSearchDocuments(e.target.value)}
                  className="w-80"
                />
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
                <DialogTrigger asChild>
                  <Button className="flex items-center space-x-2">
                    <Upload className="h-4 w-4" />
                    <span>Carregar Documento</span>
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[500px]">
                  <form onSubmit={handleUploadDocument}>
                    <DialogHeader>
                      <DialogTitle>Carregar Novo Documento</DialogTitle>
                      <DialogDescription>
                        Faça upload de um documento PDF, JPG ou PNG para assinatura eletrônica
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div className="space-y-2">
                        <Label htmlFor="title">Título do Documento *</Label>
                        <Input
                          id="title"
                          value={uploadForm.title}
                          onChange={(e) => setUploadForm({...uploadForm, title: e.target.value})}
                          placeholder="Ex: Contrato de Trabalho - João Silva"
                          required
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="description">Descrição</Label>
                        <Textarea
                          id="description"
                          value={uploadForm.description}
                          onChange={(e) => setUploadForm({...uploadForm, description: e.target.value})}
                          placeholder="Descrição opcional do documento..."
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="file">Arquivo *</Label>
                        <Input
                          id="file"
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => setUploadForm({...uploadForm, file: e.target.files[0]})}
                          required
                        />
                        <p className="text-xs text-gray-500">
                          📎 Formatos aceitos: PDF, JPG, PNG | Tamanho máximo: 30MB
                        </p>
                      </div>
                    </div>
                    <DialogFooter className="mt-6">
                      <Button variant="outline" type="button" onClick={() => setShowUploadModal(false)} disabled={uploading}>
                        Cancelar
                      </Button>
                      <Button type="submit" disabled={uploading}>
                        {uploading ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Upload className="h-4 w-4 mr-2" />
                        )}
                        {uploading ? 'Carregando...' : 'Carregar Documento'}
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>

              <Dialog open={showRequestModal} onOpenChange={setShowRequestModal}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="flex items-center space-x-2">
                    <Send className="h-4 w-4" />
                    <span>Solicitar Assinatura</span>
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
                  <form onSubmit={handleCreateSignatureRequest}>
                    <DialogHeader>
                      <DialogTitle>Nova Solicitação de Assinatura</DialogTitle>
                      <DialogDescription>
                        Envie um documento para assinatura eletrônica
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div className="space-y-2">
                        <Label htmlFor="document_select">Documento *</Label>
                        <Select value={requestForm.document_id} onValueChange={(value) => setRequestForm({...requestForm, document_id: value, signature_page: null, signature_x: null, signature_y: null})}>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione o documento" />
                          </SelectTrigger>
                          <SelectContent>
                            {documents.filter(doc =>
                              doc.status === 'draft' &&
                              doc.requires_signature === true
                            ).map(doc => (
                              <SelectItem key={doc.id} value={doc.id}>
                                {doc.title}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {requestForm.document_id && (
                        <div className="flex items-center justify-between p-3 border rounded-lg bg-gray-50">
                          <div className="text-sm">
                            <p className="font-medium">Posição da assinatura</p>
                            <p className="text-gray-500">
                              {requestForm.signature_page
                                ? `Definida na página ${requestForm.signature_page}`
                                : 'Padrão (rodapé da última página)'}
                            </p>
                          </div>
                          <Button type="button" variant="outline" size="sm" onClick={() => setShowPositionPicker(true)}>
                            {requestForm.signature_page ? 'Alterar posição' : 'Escolher posição no documento'}
                          </Button>
                        </div>
                      )}

                      <SignerCards
                        signers={requestForm.signers}
                        employees={employees}
                        onUpdate={updateSigner}
                        onRemove={removeSigner}
                        onAdd={addSigner}
                        onEmployeeSelect={(index, value) => {
                          const newSigners = [...requestForm.signers];
                          const emp = employees.find(e => e.id === value);
                          if (value && emp) {
                            newSigners[index] = { ...newSigners[index], employee_id: value, signer_name: emp.nome || '', signer_email: emp.email || '', signer_phone: emp.telefone || emp.celular || '', signer_cpf: emp.cpf || '' };
                          } else {
                            newSigners[index] = { ...newSigners[index], employee_id: value || '', signer_name: '', signer_email: '', signer_phone: '', signer_cpf: '' };
                          }
                          setRequestForm({ ...requestForm, signers: newSigners });
                        }}
                      />
                      {requestForm.signers.length > 2 && (
                        <p className="text-sm text-gray-500 text-center">{requestForm.signers.length} signatários adicionados</p>
                      )}
                    </div>
                    <DialogFooter className="mt-6">
                      <Button variant="outline" type="button" onClick={() => setShowRequestModal(false)}>
                        Cancelar
                      </Button>
                      <Button type="submit">
                        <Send className="h-4 w-4 mr-2" />
                        Enviar Solicitações
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>

              {showPositionPicker && requestForm.document_id && (
                <SignaturePositionPicker
                  documentId={requestForm.document_id}
                  initialPosition={{
                    signature_page: requestForm.signature_page,
                    signature_x: requestForm.signature_x,
                    signature_y: requestForm.signature_y
                  }}
                  onConfirm={({ signature_page, signature_x, signature_y }) => {
                    setRequestForm({ ...requestForm, signature_page, signature_x, signature_y });
                    setShowPositionPicker(false);
                  }}
                  onClose={() => setShowPositionPicker(false)}
                />
              )}

              {/* Modal URLs de Assinatura */}
              <Dialog open={showUrlsModal} onOpenChange={setShowUrlsModal}>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Links de Assinatura Criados</DialogTitle>
                    <DialogDescription>
                      Copie os links abaixo para enviar aos signatários
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    {signingUrls.map((url, index) => (
                      <div key={index} className="p-4 border rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-medium">{url.signer_name}</h4>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={async () => {
                              const fullUrl = `${window.location.origin}${url.signing_url}`;
                              await navigator.clipboard.writeText(fullUrl);
                              toast.success(`Link copiado para ${url.signer_name}!`);
                            }}
                          >
                            <Copy className="h-4 w-4 mr-2" />
                            Copiar Link
                          </Button>
                        </div>
                        <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded font-mono break-all">
                          {`${window.location.origin}${url.signing_url}`}
                        </p>
                      </div>
                    ))}
                  </div>
                  <DialogFooter>
                    <Button onClick={() => setShowUrlsModal(false)}>
                      Fechar
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              {/* Modal Envio em Lote */}
              <Dialog open={showBatchSignModal} onOpenChange={setShowBatchSignModal}>
                <DialogContent className="sm:max-w-[600px]">
                  <form onSubmit={handleBatchSignatureRequest}>
                    <DialogHeader>
                      <DialogTitle>Enviar Documentos para Assinatura</DialogTitle>
                      <DialogDescription>
                        {selectedDocsCount} documento(s) selecionado(s) - todos receberão os mesmos signatários
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                        <p className="text-sm text-purple-700 font-medium">
                          Documentos selecionados ({selectedDocsCount}):
                        </p>
                        <ul className="text-sm text-purple-600 mt-1 list-disc list-inside">
                          {selectedDocumentsList.slice(0, 5).map(doc => (
                            <li key={doc.id}>{doc.title}</li>
                          ))}
                          {selectedDocsCount > 5 && (
                            <li className="text-purple-400">...e mais {selectedDocsCount - 5}</li>
                          )}
                        </ul>
                      </div>

                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <Label className="text-base font-semibold">Signatários</Label>
                          <Button type="button" variant="outline" size="sm" onClick={addSigner} className="flex items-center space-x-1">
                            <UserPlus className="h-4 w-4" />
                            <span>Adicionar Signatário</span>
                          </Button>
                        </div>

                        <div className="max-h-[300px] overflow-y-auto pr-2 space-y-4">
                          {requestForm.signers.map((signer, index) => (
                            <div key={index} className="p-4 border rounded-lg bg-gray-50">
                              <div className="flex items-center justify-between mb-3">
                                <h4 className="font-medium">Signatário {index + 1}</h4>
                                {requestForm.signers.length > 1 && (
                                  <Button type="button" variant="outline" size="sm" onClick={() => removeSigner(index)} className="text-red-600 hover:text-red-700">
                                    <X className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                              <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                  <Label>Nome Completo *</Label>
                                  <Input value={signer.signer_name} onChange={(e) => updateSigner(index, 'signer_name', e.target.value)} placeholder="Nome completo" required />
                                </div>
                                <div className="space-y-2">
                                  <Label>Email *</Label>
                                  <Input type="email" value={signer.signer_email} onChange={(e) => updateSigner(index, 'signer_email', e.target.value)} placeholder="email@exemplo.com" required />
                                </div>
                              </div>
                              <div className="grid grid-cols-2 gap-4 mt-3">
                                <div className="space-y-2">
                                  <Label>Telefone *</Label>
                                  <Input value={signer.signer_phone} onChange={(e) => updateSigner(index, 'signer_phone', formatPhone(e.target.value))} placeholder="(11) 99999-9999" maxLength={16} required />
                                </div>
                                <div className="space-y-2">
                                  <Label>CPF *</Label>
                                  <Input value={signer.signer_cpf} onChange={(e) => updateSigner(index, 'signer_cpf', formatCPF(e.target.value))} placeholder="000.000.000-00" maxLength={14} required />
                                </div>
                              </div>
                              <div className="space-y-2 mt-3">
                                <Label>Colaborador (opcional)</Label>
                                <Autocomplete
                                  value={signer.employee_id}
                                  onValueChange={(value) => {
                                    const newSigners = [...requestForm.signers];
                                    if (value) {
                                      const emp = employees.find(e => e.id === value);
                                      if (emp) {
                                        newSigners[index] = { ...newSigners[index], employee_id: value, signer_name: emp.nome || '', signer_email: emp.email || '', signer_phone: emp.telefone || emp.celular || '', signer_cpf: emp.cpf || '' };
                                      }
                                    } else {
                                      newSigners[index] = { ...newSigners[index], employee_id: value, signer_name: '', signer_email: '', signer_phone: '', signer_cpf: '' };
                                    }
                                    setRequestForm({ ...requestForm, signers: newSigners });
                                  }}
                                  options={employees}
                                  getOptionValue={(emp) => emp.id}
                                  getOptionLabel={(emp) => `${emp.nome} - ${emp.cargo}`}
                                  placeholder="Selecione se for funcionário"
                                  emptyMessage="Nenhum funcionário encontrado"
                                />
                              </div>
                            </div>
                          ))}
                        </div>

                        <div className="flex items-center space-x-4">
                          <label className="flex items-center space-x-2">
                            <input type="checkbox" checked={requestForm.send_via_email} onChange={(e) => setRequestForm({...requestForm, send_via_email: e.target.checked})} className="h-4 w-4" />
                            <span className="text-sm">Notificar por Email</span>
                          </label>
                          <label className="flex items-center space-x-2">
                            <input type="checkbox" checked={requestForm.send_via_whatsapp} onChange={(e) => setRequestForm({...requestForm, send_via_whatsapp: e.target.checked})} className="h-4 w-4" />
                            <span className="text-sm">Notificar por WhatsApp</span>
                          </label>
                        </div>
                      </div>
                    </div>
                    <DialogFooter className="mt-6 gap-2">
                      <Button variant="outline" type="button" onClick={() => { setShowBatchSignModal(false); setSelectedDocs(new Set()); }}>
                        Cancelar
                      </Button>
                      <Button type="submit" disabled={batchSending}>
                        {batchSending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                        {batchSending ? 'Enviando...' : `Enviar para ${selectedDocsCount} documento(s)`}
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Documents Table */}
          <Card>
            <CardContent className="p-0">
              {selectedDocs.size > 0 && (
                <div className="flex items-center justify-between px-4 py-2 bg-purple-50 border-b">
                  <span className="text-sm text-purple-700 font-medium">
                    {selectedDocs.size} de {documents.filter(canSelectDoc).length} disponíveis selecionado(s)
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setSelectedDocs(new Set());
                        setShowBatchSignModal(false);
                      }}
                      className="h-8 text-xs"
                    >
                      Limpar seleção
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => {
                        const selectedList = selectedDocumentsList;
                        setShowBatchSignModal(true);
                      }}
                      className="h-8 text-xs gap-1"
                    >
                      <Send className="h-3.5 w-3.5" />
                      Enviar Selecionados
                    </Button>
                  </div>
                </div>
              )}
              <DocumentsTable
                documents={documentsPaginated}
                selectedDocs={selectedDocs}
                canSelectDoc={canSelectDoc}
                onToggleDoc={toggleDocSelection}
                onSelectAll={selectAllDocs}
                getStatusBadge={getStatusBadge}
                formatDate={formatDate}
                formatFileSize={formatFileSize}
                copyValidationCode={copyValidationCode}
                copyValidationUrl={copyValidationUrl}
                openValidationPage={openValidationPage}
                onView={handleViewDocument}
                onDownload={handleDownloadDocument}
                onDelete={handleDeleteDocument}
              />
              <GridPagination pagination={documentsPagination} onPageChange={setDocumentsPage} />
              
              {filteredDocuments.length === 0 && (
                <div className="text-center py-12">
                  <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum documento encontrado</h3>
                  <p className="text-gray-500 mb-4">Comece carregando seu primeiro documento para assinatura</p>
                  <Button onClick={() => setShowUploadModal(true)}>
                    <Upload className="h-4 w-4 mr-2" />
                    Carregar Documento
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* Requests Tab */}
        {allowedTabs.includes('requests') && (
        <TabsContent value="requests" className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <h2 className="text-xl font-semibold">Solicitações de Assinatura</h2>
              <div className="flex items-center space-x-2">
                <Search className="h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Buscar por documento, signatário ou e-mail..."
                  value={searchRequests}
                  onChange={(e) => setSearchRequests(e.target.value)}
                  className="w-80"
                />
              </div>
            </div>
            <div className="flex items-center space-x-4 text-sm">
                <div className="flex items-center space-x-1">
                  <div className="w-3 h-3 bg-gray-500 rounded-full"></div>
                  <span>Pendente: {signatureRequests.filter(r => r.status === 'pending').length}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                  <span>Enviado: {signatureRequests.filter(r => r.status === 'sent').length}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span>Assinado: {signatureRequests.filter(r => r.status === 'signed').length}</span>
                </div>
              </div>
            </div>

          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Documento</TableHead>
                    <TableHead>Signatário</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Criado em</TableHead>
                    <TableHead>Expira em</TableHead>
                    <TableHead>Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {signatureRequestsPaginated.map(request => (
                    <TableRow key={request.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{request.document?.title || 'Documento removido'}</p>
                          {request.document?.description && (
                            <p className="text-sm text-gray-500">{request.document.description}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <User className="h-4 w-4 text-gray-400" />
                          <div>
                            <p className="font-medium">{request.signer_name}</p>
                            <p className="text-sm text-gray-500 flex items-center">
                              <Mail className="h-3 w-3 mr-1" />
                              {request.signer_email}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {getRequestStatusBadge(request.status)}
                      </TableCell>
                      <TableCell>
                        {formatDate(request.created_at)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-1">
                          <Calendar className="h-4 w-4 text-gray-400" />
                          <span className={`text-sm ${
                            new Date(request.token_expires_at) < new Date() ? 'text-red-500' : 'text-gray-700'
                          }`}>
                            {formatDate(request.token_expires_at)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          {/* Botão Enviar/Reenviar Email */}
                          {['pending', 'sent', 'viewed'].includes(request.status) && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleSendEmail(request.id)}
                              title={request.status === 'pending' ? 'Enviar email com link' : 'Reenviar email com link'}
                            >
                              {request.status === 'pending'
                                ? <Send className="h-4 w-4" />
                                : <RefreshCw className="h-4 w-4" />}
                            </Button>
                          )}
                          
                          {/* Botão Copiar Link */}
                          {['pending', 'sent', 'viewed'].includes(request.status) && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => {
                                const signingUrl = `${window.location.origin}/assinatura-publica/${request.signature_token}`;
                                navigator.clipboard.writeText(signingUrl);
                                toast.success('Link de assinatura copiado!');
                              }}
                              title="Copiar link de assinatura"
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                          )}
                          
                          {/* Botão Download */}
                          {request.status === 'signed' && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => {
                                // Usar document_id diretamente se document não existir
                                const docData = request.document || {
                                  id: request.document_id,
                                  title: 'documento'
                                };
                                // Passa o id desta solicitação para trazer só a assinatura deste signatário
                                handleDownloadDocument(docData, true, request.id);
                              }}
                              title="Baixar documento assinado (só a assinatura deste signatário)"
                              disabled={!request.document && !request.document_id}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                          )}
                          
                          {/* Botão Cancelar */}
                          {['pending', 'sent', 'viewed'].includes(request.status) && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleCancelRequest(request.id)}
                              className="text-red-600 hover:text-red-700"
                              title="Cancelar solicitação"
                            >
                              <XCircle className="h-4 w-4" />
                            </Button>
                          )}
                          
                          {/* Botão Excluir */}
                          {['pending', 'sent', 'viewed', 'cancelled'].includes(request.status) && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleDeleteRequest(request.id)}
                              className="text-red-800 hover:text-red-900 border-red-300 hover:border-red-400"
                              title="Excluir permanentemente"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <GridPagination pagination={signatureRequestsPagination} onPageChange={setSignatureRequestsPage} />
              
              {filteredSignatureRequests.length === 0 && (
                <div className="text-center py-12">
                  <Send className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhuma solicitação encontrada</h3>
                  <p className="text-gray-500 mb-4">
                    As solicitações de assinatura aparecerão aqui após você enviar documentos
                  </p>
                  <Button onClick={() => setShowRequestModal(true)}>
                    <Send className="h-4 w-4 mr-2" />
                    Nova Solicitação
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* Audit Tab */}
        {allowedTabs.includes('audit') && (
        <TabsContent value="audit" className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Trilha de Auditoria</h2>
            <p className="text-sm text-gray-500">
              Histórico completo para compliance legal
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Resumo de Atividades */}
            <Card className="lg:col-span-1">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Shield className="h-5 w-5" />
                  <span>Resumo de Atividades</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <Upload className="h-4 w-4 text-blue-600" />
                    <span className="text-sm font-medium">Documentos Carregados</span>
                  </div>
                  <span className="text-lg font-bold text-blue-600">{documents.length}</span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <Send className="h-4 w-4 text-yellow-600" />
                    <span className="text-sm font-medium">Solicitações Enviadas</span>
                  </div>
                  <span className="text-lg font-bold text-yellow-600">{signatureRequests.length}</span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm font-medium">Assinaturas Concluídas</span>
                  </div>
                  <span className="text-lg font-bold text-green-600">
                    {signatureRequests.filter(r => r.status === 'signed').length}
                  </span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <Shield className="h-4 w-4 text-purple-600" />
                    <span className="text-sm font-medium">Taxa de Conformidade</span>
                  </div>
                  <span className="text-lg font-bold text-purple-600">100%</span>
                </div>
              </CardContent>
            </Card>

            {/* Atividades Recentes */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Atividades Recentes</CardTitle>
                <CardDescription>
                  Últimas ações realizadas no sistema
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Simular algumas atividades recentes */}
                  {documents.slice(0, 5).map((doc, index) => (
                    <div key={doc.id} className="flex items-start space-x-3 p-3 border rounded-lg">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <FileText className="h-4 w-4 text-blue-600" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">
                          Documento "{doc.title}" foi carregado
                        </p>
                        <p className="text-sm text-gray-500">
                          Status: {getStatusBadge(doc.status)}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          {formatDate(doc.created_at)}
                        </p>
                      </div>
                    </div>
                  ))}
                  
                  {signatureRequests.slice(0, 3).map((request, index) => (
                    <div key={request.id} className="flex items-start space-x-3 p-3 border rounded-lg">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                          <Send className="h-4 w-4 text-green-600" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">
                          Solicitação enviada para {request.signer_name}
                        </p>
                        <p className="text-sm text-gray-500">
                          Email: {request.signer_email}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          {formatDate(request.created_at)}
                        </p>
                      </div>
                      <div className="flex-shrink-0">
                        {getRequestStatusBadge(request.status)}
                      </div>
                    </div>
                  ))}
                  
                  {documents.length === 0 && signatureRequests.length === 0 && (
                    <div className="text-center py-8">
                      <Shield className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-sm text-gray-500">
                        Nenhuma atividade registrada ainda
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Informações de Compliance */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Shield className="h-5 w-5 text-green-600" />
                <span>Conformidade Legal</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <CheckCircle className="h-8 w-8 text-green-600 mx-auto mb-2" />
                  <p className="text-sm font-medium">MP 2.200-2/2001</p>
                  <p className="text-xs text-gray-500">Conformidade Legal</p>
                </div>
                
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <Shield className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                  <p className="text-sm font-medium">Certificação Digital</p>
                  <p className="text-xs text-gray-500">Todas as Assinaturas</p>
                </div>
                
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <Eye className="h-8 w-8 text-purple-600 mx-auto mb-2" />
                  <p className="text-sm font-medium">Trilha de Auditoria</p>
                  <p className="text-xs text-gray-500">100% Rastreável</p>
                </div>
                
                <div className="text-center p-4 bg-yellow-50 rounded-lg">
                  <Clock className="h-8 w-8 text-yellow-600 mx-auto mb-2" />
                  <p className="text-sm font-medium">Timestamp</p>
                  <p className="text-xs text-gray-500">Certificado</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* Advanced Audit Tab */}
        {allowedTabs.includes('audit-advanced') && (
        <TabsContent value="audit-advanced" className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <h2 className="text-xl font-semibold">Auditoria Avançada</h2>
              <div className="flex items-center space-x-2">
                <Search className="h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Buscar por ação, documento, signatário ou usuário..."
                  value={searchAuditLogs}
                  onChange={(e) => setSearchAuditLogs(e.target.value)}
                  className="w-80"
                />
              </div>
            </div>
            <p className="text-sm text-gray-500">
              Logs detalhados de todas as operações do sistema
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            {/* Filtros */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Filtros</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label className="text-xs">Ação</Label>
                  <select className="w-full p-2 border rounded text-sm">
                    <option value="">Todas as ações</option>
                    <option value="document_uploaded">Upload de documento</option>
                    <option value="signature_request_created">Solicitação criada</option>
                    <option value="email_sent">Email enviado</option>
                    <option value="document_viewed">Documento visualizado</option>
                    <option value="document_signed">Documento assinado</option>
                    <option value="signature_request_cancelled">Solicitação cancelada</option>
                  </select>
                </div>
                <Button size="sm" className="w-full">
                  <RefreshCw className="h-3 w-3 mr-1" />
                  Filtrar
                </Button>
              </CardContent>
            </Card>

            {/* Logs */}
            <Card className="lg:col-span-3">
              <CardHeader>
                <CardTitle>Logs de Auditoria</CardTitle>
                <CardDescription>
                  Últimas {filteredAuditLogs.length} operações registradas
                </CardDescription>
              </CardHeader>
              <CardContent>
                {filteredAuditLogs.length > 0 ? (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {filteredAuditLogs.map((log, index) => (
                      <div key={log.id || index} className="border rounded-lg p-3 bg-gray-50">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <Badge variant="outline" className="text-xs">
                                {log.action}
                              </Badge>
                              <span className="text-xs text-gray-500">
                                {log.timestamp ? new Date(log.timestamp).toLocaleString('pt-BR') : 'N/A'}
                              </span>
                            </div>
                            
                            <p className="text-sm font-medium text-gray-900 mb-1">
                              {log.description || 'Ação do sistema'}
                            </p>
                            
                            <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                              {log.document_title && (
                                <div>
                                  <span className="font-medium">Documento:</span> {log.document_title}
                                </div>
                              )}
                              {log.signer_name && (
                                <div>
                                  <span className="font-medium">Signatário:</span> {log.signer_name}
                                </div>
                              )}
                              {log.user_name && (
                                <div>
                                  <span className="font-medium">Usuário:</span> {log.user_name}
                                </div>
                              )}
                              {log.ip_address && (
                                <div>
                                  <span className="font-medium">IP:</span> {log.ip_address}
                                </div>
                              )}
                            </div>

                            {log.metadata && Object.keys(log.metadata).length > 0 && (
                              <details className="mt-2">
                                <summary className="text-xs text-blue-600 cursor-pointer">
                                  Ver Metadados
                                </summary>
                                <table className="w-full text-xs mt-1 border rounded overflow-hidden">
                                  <tbody>
                                    {Object.entries(log.metadata).map(([key, value]) => (
                                      <tr key={key} className="border-b last:border-b-0 odd:bg-white even:bg-gray-100">
                                        <td className="px-2 py-1 font-medium text-gray-600 align-top whitespace-nowrap w-1/3">
                                          {formatMetadataKey(key)}
                                        </td>
                                        <td className="px-2 py-1 text-gray-800 break-all">
                                          {formatMetadataValue(key, value)}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </details>
                            )}
                          </div>

                          <div className="flex items-center space-x-1 text-xs text-gray-400">
                            {log.action === 'document_signed' && <CheckCircle className="h-3 w-3 text-green-500" />}
                            {log.action === 'document_uploaded' && <Upload className="h-3 w-3 text-blue-500" />}
                            {log.action === 'email_sent' && <Send className="h-3 w-3 text-purple-500" />}
                            {log.action === 'document_viewed' && <Eye className="h-3 w-3 text-yellow-500" />}
                            {log.action === 'signature_request_cancelled' && <XCircle className="h-3 w-3 text-red-500" />}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Shield className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm text-gray-500">
                      Nenhum log de auditoria encontrado
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Estatísticas Técnicas */}
          <Card>
            <CardHeader>
              <CardTitle>Informações Técnicas de Segurança</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <Shield className="h-8 w-8 text-green-600 mx-auto mb-2" />
                  <p className="text-sm font-medium">Hash SHA-256</p>
                  <p className="text-xs text-gray-500">Integridade garantida</p>
                </div>
                
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <Clock className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                  <p className="text-sm font-medium">Timestamp ISO 8601</p>
                  <p className="text-xs text-gray-500">Precisão temporal</p>
                </div>
                
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <User className="h-8 w-8 text-purple-600 mx-auto mb-2" />
                  <p className="text-sm font-medium">Registro de IP</p>
                  <p className="text-xs text-gray-500">Rastreabilidade total</p>
                </div>
                
                <div className="text-center p-4 bg-yellow-50 rounded-lg">
                  <FileText className="h-8 w-8 text-yellow-600 mx-auto mb-2" />
                  <p className="text-sm font-medium">Não-repúdio</p>
                  <p className="text-xs text-gray-500">MP 2.200-2/2001</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default DigitalSignaturePage;